<script lang="ts">
    import { onMount } from 'svelte/internal'
    import type { UpsertProfileDto } from '../../../modules/toolsApi/sites/profiles/dto/upsert-profile.dto'
    import { Profile } from '../../../modules/toolsApi/sites/profiles/profile'
    import Zact from '../../_common/z/zact.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine, GridToggle } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import { Link } from '../../../modules/toolsApi/sites/links/link'
    import Popover from '../../_common/popover.svelte'
    import SiteLinkEdit from './site-link-edit.svelte'
    import { ProfileService } from '../../../modules/toolsApi/sites/services/profile-service'
    import SiteServiceEdit from './site-service-edit.svelte'
    import { faAdd, faCopy, faEdit, faSave, faStar, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import {
        SchedulerAppointmentType,
        SchedulerAppointmentTypeFriendly,
        SchedulerStartBy,
        SchedulerStartByFriendly,
    } from '../../../modules/toolsApi/sites/profiles/profile.enum'
    import Zinput from '../../_common/z/zinput.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import type { Review } from '../../../modules/toolsApi/nps/reviews/review'
    import { ReviewType } from '../../../modules/toolsApi/nps/reviews/reviews.enum'

    let show = true
    let entity: Profile = new Profile()
    let active: boolean = true

    // opção vazia explícita: sem ela o select fica sem seleção visível enquanto o valor está vazio
    const onlyWithOptions = [
        { value: '', label: '' },
        ...Object.entries(SchedulerAppointmentType).map(([key, value]) => ({ value, label: SchedulerAppointmentTypeFriendly[key] })),
    ]

    const startByOptions = Object.entries(SchedulerStartBy).map(([key, value]) => ({ value, label: SchedulerStartByFriendly[key] }))

    onMount(async () => {
        await loadEntity()
    })

    async function loadEntity() {
        entity = await window.api.tools.sites.profiles.getOne(null)
        if (!entity) {
            newEntity()
        }
        loadGridLinks()
        loadGridServices()
        show = false
        active = entity.status === 100
        console.log('profile', entity)
    }

    function newEntity() {
        entity = new Profile()
        entity.themeBackgroundColor = '#DDDDDD'
        entity.themeTextColor = '#222222'
        entity.themeHeaderBackgroundColor = '#357DC0'
        entity.themeHeaderTextColor = '#FFFFFF'
        entity.themePrimaryColor = '#357DC0'

        entity.timezone = window.store.company.timezone
        entity.schedulerOnline = true
        entity.schedulerStartBy = SchedulerStartBy.scheduler
        entity.schedulerAllowConsulting = true
        entity.schedulerAllowMultiple = true
        entity.schedulerMissesLimit = 0
        entity.schedulerOnlyWith = 'all'
        entity.schedulerPreviousTimeMaxDays = 90
        entity.schedulerShowPrice = false
        entity.schedulerShowDescription = false
        entity.schedulerBlockDuplicity = true
        entity.schedulerPreviousTimeMinMinutes = 120
        entity.googleShowReviews = false
        entity.googleShowRating = true
        active = true
    }

    async function upsertEntity() {
        try {
            if (show) return
            show = true
            const dto: UpsertProfileDto = {
                slug: entity.slug,
                domain: entity.domain,
                profileBio: entity.profileBio,
                profileTitle: entity.profileTitle,
                seoDescription: entity.seoDescription,
                seoTitle: entity.seoTitle,
                themeBackgroundColor: entity.themeBackgroundColor,
                themeFavicon: entity.themeFavicon,
                themePrimaryColor: entity.themePrimaryColor,
                themeSecondaryColor: entity.themeSecondaryColor,
                links: entity.links,
                services: entity.services,
                schedulerOnline: entity.schedulerOnline,
                schedulerPreviousTimeMaxDays: entity.schedulerPreviousTimeMaxDays,
                schedulerPreviousTimeMinMinutes: entity.schedulerPreviousTimeMinMinutes,
                schedulerShowPrice: entity.schedulerShowPrice,
                schedulerAllowConsulting: entity.schedulerAllowConsulting,
                schedulerBlockDuplicity: entity.schedulerBlockDuplicity,
                themeTextColor: entity.themeTextColor,
                themeHeaderTextColor: entity.themeHeaderTextColor,
                themeHeaderBackgroundColor: entity.themeHeaderBackgroundColor,
                schedulerMissesLimit: entity.schedulerMissesLimit,
                schedulerOnlyWith: entity.schedulerOnlyWith,
                schedulerOnlywithRecentDays: entity.schedulerOnlywithRecentDays,
                schedulerAllowMultiple: entity.schedulerAllowMultiple,
                schedulerStartBy: entity.schedulerStartBy || SchedulerStartBy.scheduler,
                schedulerShowDescription: entity.schedulerShowDescription,
                gpsAddress: entity.gpsAddress,
                googleShowReviews: entity.googleShowReviews,
                googleShowRating: entity.googleShowRating,
                themeHeaderFormat: entity.themeHeaderFormat,
                status: active ? 100 : 70,
            }
            if (!validateDto(dto)) return false
            entity = await window.api.tools.sites.profiles.create(dto)
            loadGridLinks()
            loadGridServices()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto: UpsertProfileDto) {
        if (!dto.slug) {
            window.error('Informe seu endereço.\nEste é o endereço em que seus clientes vão encontrar seu aplicativo.')
            return false
        }
        for (const char of dto.slug.split('')) {
            if (!validChars.includes(char)) {
                window.error('Seu endereço deve possuir apenas letras, números e _')
                return false
            }
        }
        if (!dto.profileTitle) {
            window.error('Informe seu nome de perfil\nPor exemplo o nome de sua empresa.')
            return false
        }
        if (dto.schedulerOnlyWith === 'recent' && dto.schedulerOnlywithRecentDays <= 0) {
            window.error('O campo "Exibir serviços realizados até" deve ser maior que 0 (zero)')
            return false
        }
        return true
    }

    let grid: GridClass = new GridClass()
    function loadGridLinks() {
        // grid
        grid = new GridClass()
        // grid head
        grid.showActs = false
        grid.title = 'Links'
        grid.acts = [
            new GridButton({
                label: 'Adicionar',
                icon: faAdd,
                callback: () => {
                    editLink()
                },
            }),
        ]
        grid.subtitle = 'Adicione links para suas redes sociais, WhatsApp e telefone'
        grid.cols.push(new GridCol({ content: 'Tipo de link', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Título' }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        if (entity.links) {
            for (const [index, link] of entity.links?.entries()) {
                let line: GridLine = new GridLine()
                line.slots.push({ content: link.type })
                line.slots.push({ content: link.title })
                line.slots.push({ content: link.url })
                line.slots.push({
                    content: [
                        new GridButton({
                            label: 'Excluir',
                            icon: faTrashAlt,
                            color: 'red',
                            callback: () => {
                                removeLink(index)
                            },
                        }),
                        new GridButton({
                            label: 'Editar',
                            icon: faEdit,
                            callback: () => {
                                editLink(index)
                            },
                        }),
                    ],
                })
                grid.lines.push(line)
            }
        }
    }

    let link: Link = null
    let linkIndex: number = null
    function editLink(index: number = null) {
        linkIndex = index
        if (index !== null) {
            link = entity.links[index]
            return
        }
        link = new Link()
    }

    function removeLink(index) {
        if (!confirm('Deseja mesmo excluir este link?')) return
        entity.links.splice(index, 1)
        loadGridLinks()
        window.alert('Link removido da lista.\nLembre-se de clicar em "Salvar" para confirmar as alterações.')
    }

    function onLinkSuccess(ev) {
        if (linkIndex !== null) {
            entity.links[linkIndex] = link
        } else {
            entity.links.push(ev.detail)
        }
        link = null
        loadGridLinks()
    }

    let gridServices: GridClass = new GridClass()
    function loadGridServices() {
        gridServices = new GridClass()
        gridServices.showActs = false
        gridServices.title = 'Nossos serviços'
        gridServices.subtitle = 'Apresente seus serviços na tela inicial do aplicativo. A ordem da lista é a ordem em que eles aparecem'
        gridServices.acts = [
            new GridButton({
                label: 'Adicionar',
                icon: faAdd,
                callback: () => {
                    editService()
                },
            }),
        ]
        gridServices.cols.push(new GridCol({ content: 'Título' }))
        gridServices.cols.push(new GridCol({ content: 'Subtítulo' }))
        gridServices.cols.push(new GridCol({ content: 'Visível', type: GridColTypeEnum.string }))
        gridServices.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        if (entity.services) {
            for (const [index, service] of entity.services.entries()) {
                let line: GridLine = new GridLine()
                line.slots.push({ content: service.headline })
                line.slots.push({ content: service.subheadline })
                line.slots.push({ content: service.status === 70 ? 'Não' : 'Sim' })
                line.slots.push({
                    content: [
                        new GridButton({
                            label: 'Excluir',
                            icon: faTrashAlt,
                            color: 'red',
                            callback: () => {
                                removeService(index)
                            },
                        }),
                        new GridButton({
                            label: 'Editar',
                            icon: faEdit,
                            callback: () => {
                                editService(index)
                            },
                        }),
                    ],
                })
                gridServices.lines.push(line)
            }
        }
    }

    let service: ProfileService = null
    let serviceIndex: number = null
    function editService(index: number = null) {
        serviceIndex = index
        if (index !== null) {
            service = entity.services[index]
            return
        }
        service = new ProfileService()
    }

    function removeService(index) {
        if (!confirm('Deseja mesmo excluir este serviço?')) return
        entity.services.splice(index, 1)
        loadGridServices()
        window.alert('Serviço removido da lista.\nLembre-se de clicar em "Salvar" para confirmar as alterações.')
    }

    function onServiceSuccess(ev) {
        if (serviceIndex !== null) {
            entity.services[serviceIndex] = service
        } else {
            // o perfil recém-criado ainda não tem a lista
            if (!entity.services) entity.services = []
            entity.services.push(ev.detail)
        }
        service = null
        loadGridServices()
    }

    let showReviews = false
    let reviews: Review[] = []
    let reviewsGrid: GridClass = new GridClass()

    const reviewsActs: GridButton[] = [
        new GridButton({
            label: 'Ver avaliações',
            icon: faStar,
            mobileShowLabel: true,
            callback: () => openReviews(),
        }),
    ]

    async function openReviews() {
        showReviews = true
        await loadReviews()
    }

    async function loadReviews() {
        try {
            // includePaused: a tela precisa listar as desativadas pra poder reativar
            reviews = await window.api.tools.nps.reviews.getAll({ type: ReviewType.google, includePaused: true })
            loadGridReviews()
        } catch (error) {
            window.error(error)
        }
    }

    function loadGridReviews() {
        reviewsGrid = new GridClass()
        reviewsGrid.showActs = false
        reviewsGrid.subtitle = 'Desative as avaliações que não devem aparecer em seu aplicativo'
        reviewsGrid.cols.push(new GridCol({ content: 'Ativa', type: GridColTypeEnum.toggle, size: 1 }))
        reviewsGrid.cols.push(new GridCol({ content: 'Autor', type: GridColTypeEnum.string }))
        reviewsGrid.cols.push(new GridCol({ content: 'Nota', type: GridColTypeEnum.int, size: 1 }))
        reviewsGrid.cols.push(new GridCol({ content: 'Avaliação', type: GridColTypeEnum.string, size: 5 }))
        reviewsGrid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.date, size: 2 }))
        for (const review of reviews) {
            const toggle = new GridToggle({
                checked: review.status === 100,
                callback: () => toggleReviewStatus(review.reviewId),
            })

            let line: GridLine = new GridLine()
            line.slots.push({ content: toggle })
            line.slots.push({ content: review.authorName })
            line.slots.push({ content: review.rating })
            line.slots.push({ content: review.text })
            line.slots.push({ content: review.createdAt })
            reviewsGrid.lines.push(line)
        }
    }

    async function toggleReviewStatus(reviewId: string) {
        try {
            const current = reviews.find((el) => el.reviewId === reviewId)
            await window.api.tools.nps.reviews.setStatus(reviewId, current.status === 100 ? 70 : 100)
            await loadReviews()
        } catch (error) {
            window.error(error)
        }
    }

    const validChars = 'abcdefghijklmnopqrstuvwxyz_0123456789'

    // O campo de endereço antes bloqueava a tecla no keypress. O Zinput não repassa esse evento, e
    // limpar o que foi digitado cobre mais casos do que o bloqueio cobria — inclusive colar, que
    // passava direto pelo filtro antigo. A validação no salvar segue sendo a garantia final.
    function sanitizeSlug() {
        if (!entity.slug) return
        const clean = entity.slug
            .toLowerCase()
            .split('')
            .filter((char) => validChars.includes(char))
            .join('')
        if (clean !== entity.slug) entity.slug = clean
    }

    function copySlug() {
        window.api.utils.utils.copyToClipboard('https://agenda.zandu.com.br/' + entity.slug)
        window.alert('Link copiado')
    }

    const slugActs: GridButton[] = [
        {
            label: '',
            icon: faCopy,
            callback: copySlug,
        },
    ]
</script>

{#if showReviews}
    <Popover title="Avaliações do Google" width="900px" height="480px" on:close={() => (showReviews = false)}>
        <Zgrid class="m-3" grid={reviewsGrid} />
    </Popover>
{/if}

{#if link}
    <Popover title="Link" width="720px" height="100px" on:close={() => (link = null)}>
        <SiteLinkEdit on:success={onLinkSuccess} bind:link />
    </Popover>
{/if}

{#if service}
    <Popover title="Serviço" width="860px" height="100px" on:close={() => (service = null)}>
        <SiteServiceEdit on:success={onServiceSuccess} bind:service />
    </Popover>
{/if}

{#if !show}
    <Zact>
        <div class="spacer" />
        <Zbutton color="primary" icon={faSave} label="Salvar" loading={show} disabled={show} on:click={upsertEntity} />
    </Zact>
    <Zframe class="h-space" title="Perfil" headline="Configure os dados de perfil de seu app">
        <ZContainer gapRow="8px" gapCol="8px">
            <Zinput size={12} skin="outline" label="Aplicativo ativo" type="toggle" bind:value={active} />
            <Zinput size={6} sizeM={12} label="Endereço: agenda.zandu.com.br/" type="text" acts={slugActs} bind:value={entity.slug} on:keyup={sanitizeSlug} />
            <Zinput size={6} sizeM={12} label="Nome de perfil" type="text" bind:value={entity.profileTitle} />
            <Zinput size={12} sizeM={12} label="Texto da bio" type="text" bind:value={entity.profileBio} />
        </ZContainer>
    </Zframe>

    <div>
        <Zgrid class="h-space" {grid} />
    </div>

    <div>
        <Zgrid class="h-space" grid={gridServices} />
    </div>

    <Zframe class="h-space" title="Google Meu Negócio" headline="Mostre o mapa e as avaliações do seu negócio para seus clientes" acts={reviewsActs}>
        <ZContainer gapRow="8px" gapCol="8px">
            <Zinput size={12} label="Seu endereço completo" type="text" bind:value={entity.gpsAddress} />
            <ZContainerCol size={12}>
                <div class="text-muted">Separe apenas por vírgula. Siga este padrão: Rua Principal, 2300, Centro, São Paulo, SP</div>
            </ZContainerCol>

            <Zinput size={6} label="Exibir melhores avaliações" type="toggle" skin="outline" bind:value={entity.googleShowReviews} />

            <Zinput
                disabled={!entity.googleShowReviews}
                size={6}
                label="Exibir nota da empresa"
                type="toggle"
                skin="outline"
                bind:value={entity.googleShowRating}
            />
        </ZContainer>
    </Zframe>

    <Zframe class="h-space" title="Agendamento Online" headline="Configure seu aplicativo de agendamento online">
        <ZContainer gapRow="8px" gapCol="8px">
            <Zinput size={12} label="Permitir agendamento online" type="toggle" skin="outline" bind:value={entity.schedulerOnline} />

            <Zinput
                disabled={!entity.schedulerOnline}
                size={6}
                sizeM={12}
                label="Iniciar o agendamento"
                type="select"
                options={startByOptions}
                bind:value={entity.schedulerStartBy}
            />

            <Zinput
                disabled={!entity.schedulerOnline}
                size={6}
                sizeM={12}
                label="Serviços disponíveis para agendamento"
                type="select"
                options={onlyWithOptions}
                bind:value={entity.schedulerOnlyWith}
            />
            {#if entity.schedulerOnlyWith === SchedulerAppointmentType.recent}
                <Zinput
                    disabled={!entity.schedulerOnline}
                    size={6}
                    sizeM={12}
                    label="Exibir serviços realizados até (dias atrás)"
                    type="number"
                    bind:value={entity.schedulerOnlywithRecentDays}
                />
            {/if}

            <Zinput
                disabled={!entity.schedulerOnline}
                size={6}
                sizeM={12}
                label="O cliente pode agendar no máximo (minutos antes do horário escolhido)"
                type="number"
                bind:value={entity.schedulerPreviousTimeMinMinutes}
            />
            <Zinput
                disabled={!entity.schedulerOnline}
                size={6}
                sizeM={12}
                label="O cliente pode criar agendamentos com até (dias no futuro)"
                type="number"
                bind:value={entity.schedulerPreviousTimeMaxDays}
            />
            <Zinput
                disabled={!entity.schedulerOnline}
                size={6}
                sizeM={12}
                label="Bloquear agendamento de clientes que não compareceram (ou mais vezes seguidas)"
                type="number"
                bind:value={entity.schedulerMissesLimit}
            />

            <Zinput
                disabled={!entity.schedulerOnline}
                size={12}
                label="Permitir agendar serviços marcados como 'avaliação' independente das configurações acima"
                type="toggle"
                skin="outline"
                bind:value={entity.schedulerAllowConsulting}
            />
            <Zinput
                disabled={!entity.schedulerOnline}
                size={12}
                label="Exibir o preço dos serviços"
                type="toggle"
                skin="outline"
                bind:value={entity.schedulerShowPrice}
            />
            <Zinput
                disabled={!entity.schedulerOnline}
                size={12}
                label="Exibir a descrição dos serviços"
                type="toggle"
                skin="outline"
                bind:value={entity.schedulerShowDescription}
            />
            <Zinput
                disabled={!entity.schedulerOnline}
                size={12}
                label="Permitir agendar mais de um serviço por agendamento"
                type="toggle"
                skin="outline"
                bind:value={entity.schedulerAllowMultiple}
            />
            <Zinput
                disabled={!entity.schedulerOnline}
                size={12}
                label="Bloquear novos agendamentos de um serviço caso o cliente já possua este serviço agendado no futuro"
                type="toggle"
                skin="outline"
                bind:value={entity.schedulerBlockDuplicity}
            />

            <ZContainerCol size={12}>
                <div class="text-muted">
                    Lembre-se de configurar a duração de cada serviço em 'Ferramentas &gt; Serviços'.<br />
                    Configure quais agendas e quais horários estão disponíveis online em 'Ferramentas &gt; Minhas Agendas'.
                </div>
            </ZContainerCol>
        </ZContainer>
    </Zframe>
{/if}
